/**
 * CanvasEditor — full-screen orchestrator for a single canvas.
 *
 * Owns the CanvasCore ref, fetches/saves the project, wires keyboard shortcuts,
 * autosave (30s) and the image picker. Renders as a fixed overlay that takes
 * over the viewport (covers the role nav) like Figma/Miro; the back button
 * returns to the listing.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from '../../../utils/toast';
import { T } from '../../../components/em/ui';
import { wsBase } from '../wsApi';
import canvasApi from './canvasApi';
import { useCanvasStore } from './canvasStore';
import { SHORTCUT_MAP, AUTOSAVE_INTERVAL } from './canvasConstants';
import CanvasCore from './CanvasCore';
import ExportDialog from './ExportDialog';
import TemplatesDialog from './TemplatesDialog';
import HistoryDialog from './HistoryDialog';
import PresentationMode from './PresentationMode';
import CanvasPresence from './CanvasPresence';
import { useCanvasCollaboration } from './useCanvasCollaboration';
import { FloatingToolbar, TopActionBar, ZoomControls, RightPanel, ContextMenu, MiniMap } from './CanvasChrome';
import './canvas.css';

export default function CanvasEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fileRef = useRef(null);

  const [name, setName] = useState('Untitled canvas');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [presenting, setPresenting] = useState(false);

  const coreReady = useRef(false);
  const fetched = useRef(null);
  const loadedOnce = useRef(false);

  const setActiveTool = useCanvasStore((s) => s.setActiveTool);
  const activeTool = useCanvasStore((s) => s.activeTool);
  const resetEditor = useCanvasStore((s) => s.resetEditor);
  const setDirty = useCanvasStore((s) => s.setDirty);

  const backTo = `${wsBase()}/canvas`;

  // Real-time collaboration (Yjs + Socket.IO). broadcastLocal is fed by
  // CanvasCore's onChange; remote updates are applied inside the hook.
  const collab = useCanvasCollaboration(id, canvasRef);

  // Load the project once both the data and the Fabric canvas are ready.
  const tryLoad = useCallback(async () => {
    if (loadedOnce.current || !coreReady.current || !fetched.current) return;
    loadedOnce.current = true;
    const rec = fetched.current;
    setName(rec.name || 'Untitled canvas');
    await canvasRef.current?.loadJSON(rec.canvas_json || null);
    canvasRef.current?.fitToScreen();
    setLoading(false);
  }, []);

  useEffect(() => {
    let alive = true;
    // Reset the per-board load guards so a route param change (canvas → canvas,
    // or browser back/forward between two boards) actually reloads the new one.
    // CanvasCore is NOT remounted on a param change, so coreReady stays true on
    // purpose — only loadedOnce/fetched must be cleared for tryLoad to re-run.
    loadedOnce.current = false;
    fetched.current = null;
    setLoading(true);
    resetEditor();
    canvasApi.get(id)
      .then((rec) => { if (alive) { fetched.current = rec; tryLoad(); } })
      .catch(() => { toast.error('Canvas not found'); navigate(backTo); });
    return () => { alive = false; resetEditor(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onCoreReady = useCallback(() => { coreReady.current = true; tryLoad(); }, [tryLoad]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!canvasRef.current) return;
    setSaving(true);
    try {
      await canvasApi.update(id, {
        name: name.trim() || 'Untitled canvas',
        canvas_json: canvasRef.current.getJSON(),
        thumbnail: canvasRef.current.thumbnail(),
      });
      setDirty(false);
    } catch {
      toast.error('Could not save canvas');
    } finally {
      setSaving(false);
    }
  }, [id, name, setDirty]);

  // Autosave when dirty.
  useEffect(() => {
    const t = setInterval(() => {
      if (useCanvasStore.getState().dirty && !saving) save();
    }, AUTOSAVE_INTERVAL);
    return () => clearInterval(t);
  }, [save, saving]);

  const onRename = (v) => { setName(v); setDirty(true); };

  // ── Image picker (triggered when the Image tool is chosen) ───────────────────
  useEffect(() => {
    if (activeTool === 'image') { fileRef.current?.click(); setActiveTool('select'); }
  }, [activeTool, setActiveTool]);

  // ── Frame tool is a one-shot insert (like Image) ─────────────────────────────
  useEffect(() => {
    if (activeTool === 'frame') { canvasRef.current?.insertFrame(); setActiveTool('select'); }
  }, [activeTool, setActiveTool]);

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) await canvasRef.current?.addImageFromFile(file);
  };

  // ── Keyboard shortcuts ────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const ae = document.activeElement;
      const typing = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT' || ae.isContentEditable);
      const mod = e.metaKey || e.ctrlKey;
      const api = canvasRef.current;

      if (mod) {
        const k = e.key.toLowerCase();
        if (k === 's') { e.preventDefault(); save(); return; }
        if (typing) return; // let the browser handle text editing combos
        if (k === 'z') { e.preventDefault(); e.shiftKey ? api?.redo() : api?.undo(); return; }
        if (k === 'y') { e.preventDefault(); api?.redo(); return; }
        if (k === 'c') { api?.copySelected(); return; }
        if (k === 'v') { api?.paste(); return; }
        if (k === 'd') { e.preventDefault(); api?.duplicateSelected(); return; }
        if (k === 'a') { e.preventDefault(); api?.selectAll(); return; }
        return;
      }

      if (typing) return;

      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); api?.deleteSelected(); return; }
      if (e.key === 'Escape') { api?.discardSelection(); setActiveTool('select'); return; }
      const tool = SHORTCUT_MAP[e.key.toLowerCase()];
      if (tool) { e.preventDefault(); setActiveTool(tool); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save, setActiveTool]);

  return (
    <div className="cv-editor">
      <CanvasCore ref={canvasRef} onReady={onCoreReady} onChange={collab.broadcastLocal} />

      {/* Chrome */}
      <div className="cv-topbar-wrap">
        <TopActionBar
          name={name} onRename={onRename} onBack={() => navigate(backTo)}
          onSave={save} saving={saving} onExport={() => setShowExport(true)}
          onTemplates={() => setShowTemplates(true)}
          onHistory={() => setShowHistory(true)} onPresent={() => setPresenting(true)}
          presence={<CanvasPresence active={collab.active} users={collab.users} />}
          api={canvasRef.current}
        />
      </div>
      <div className="cv-toolbar-wrap"><FloatingToolbar /></div>
      <div className="cv-rightpanel-wrap"><RightPanel api={canvasRef.current} /></div>
      <div className="cv-minimap-wrap"><MiniMap api={canvasRef.current} /></div>
      <div className="cv-zoom-wrap"><ZoomControls api={canvasRef.current} /></div>

      <ContextMenu api={canvasRef.current} />
      {showExport && <ExportDialog api={canvasRef.current} name={name} onClose={() => setShowExport(false)} />}
      {showTemplates && <TemplatesDialog api={canvasRef.current} onClose={() => setShowTemplates(false)} />}
      {showHistory && (
        <HistoryDialog
          canvasId={id}
          onBeforeSnapshot={save}
          onRestored={async (rec) => { if (rec?.canvas_json) { await canvasRef.current?.loadJSON(rec.canvas_json); canvasRef.current?.fitToScreen(); } }}
          onClose={() => setShowHistory(false)}
        />
      )}
      {presenting && <PresentationMode api={canvasRef.current} onClose={() => setPresenting(false)} />}

      {loading && (
        <div className="cv-loading" style={{ font: `500 14px ${T.font}`, color: T.fg5 }}>Loading canvas…</div>
      )}

      <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} style={{ display: 'none' }} />
    </div>
  );
}
